/* global console navigator ClipboardItem Blob window __APP_VERSION__ */
import * as React from "react";
import { useState, useEffect } from "react";
import { Menu, MenuTrigger, MenuList, MenuPopover, Divider, Tooltip } from "@fluentui/react-components";
import { useDispatch, useSelector, useStore } from "react-redux";
import { clear, reset } from "../../state/structureSlice";
import { reset as selectionReset } from "../../state/selectionSlice";
import { reset as styleReset } from "../../state/cellStylesSlice";
import { init } from "../../state/bordersSlice";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { openPreferencesDialog, openStyleSheetDialog, toTemplates } from "../../state/appStateSlice";
import { t } from "i18next";
import { notify } from "../providers/GlobalToast";
import BrowserDetect from "../../helpers/browserDetect";
import { TUTORIAL_URL } from "../../constants/downloads";
import { batchGroupBy } from "../../helpers/batchGroupBy";
import { storeSheets } from "../../state/styleSheetSlice";
import {
  BugIcon,
  ClipboardIcon,
  CopyIcon,
  EraserIcon,
  GearFineIcon,
  GridNineIcon,
  PaintBrushBroadIcon,
  PlusIcon,
  PresentationChartIcon,
  SlidersIcon,
  SwapIcon,
  TrashIcon,
  WrenchIcon,
} from "@phosphor-icons/react";
import TrackedButton from "../common/TrackedButton";
import TrackedMenuItem from "../common/TrackedMenuItem";
import CommonTooltip from "../common/CommonTooltip";
import { insertSlideWithGridd } from "../../ppt/pptapi";
import { exportGriddDataToJSON } from "../../helpers/fileSystem";
BrowserDetect.init();

const GriddMenu = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [importing, setImporting] = useState(false);
  const [mouseOverDocument, setMouseOverDocument] = useState(false);
  const welcomeTemplate = useSelector((state) => state.templates.welcome);
  const uiScale = useSelector((state) => state.preferences.uiScale);
  const advancedMenu = useSelector((state) => state.preferences.advancedMenu);

  useEffect(() => {
    function handleMouseMove() {
      setMouseOverDocument(true);
    }

    function handleMouseLeave() {
      setMouseOverDocument(false);
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleMouseLeave);
  }, []);

  const openTutorial = () => {
    window.open(TUTORIAL_URL, "_blank", "width=1220,height=800");
  };

  return (
    <Menu>
      <MenuTrigger>
        <Tooltip content={t("tooltip.menu")} positioning="after" showDelay={2000}>
          <div
            style={{
              visibility: mouseOverDocument ? "visible" : "hidden",
              backgroundColor: "#FFFFFFCC",
            }}
          >
            <TrackedButton
              id="gridd_menu_button"
              style={{
                transform: `scale(${uiScale ?? 1})`,
              }}
              appearance="subtle"
              size="small"
              icon={<GearFineIcon weight="light" />}
            />
          </div>
        </Tooltip>
      </MenuTrigger>

      <MenuPopover id="gridd_menu_popover">
        <MenuList>
          <CommonTooltip content={t("tooltip.preferences")} positioning="after">
            <TrackedMenuItem
              id="preferences_button"
              icon={<SlidersIcon weight="light" />}
              onClick={() => {
                dispatch(openPreferencesDialog());
              }}
            >
              {t("menu.preferences.title")}
            </TrackedMenuItem>
          </CommonTooltip>

          <CommonTooltip content={t("menu.starterPresentation.tooltip")} positioning="after">
            <TrackedMenuItem
              icon={<PresentationChartIcon weight="light" />}
              id="starter_presentation_button"
              onClick={(e) => {
                e.preventDefault();
                openTutorial();
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {t("menu.starterPresentation")}
            </TrackedMenuItem>
          </CommonTooltip>

          <Menu>
            <MenuTrigger>
              <TrackedMenuItem id="manage_gridd_button" icon={<WrenchIcon />}>
                {t("menu.manage")}
              </TrackedMenuItem>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <CommonTooltip
                  content={
                    <div style={{ fontSize: 10 }}>
                      <span>{t("tooltip.debug")}</span>
                      <br />
                      <span>
                        {BrowserDetect.browser} {BrowserDetect.version} - {BrowserDetect.OS}
                        <br />
                        {navigator.userAgent}
                        <br />
                        Px ratio: {window.devicePixelRatio}
                      </span>
                      <br />
                      <span style={{ fontWeight: "bold" }}>Gridd: {__APP_VERSION__}</span>
                    </div>
                  }
                >
                  <TrackedMenuItem
                    id="debug_copy_button"
                    icon={<BugIcon />}
                    onClick={() => {
                      const stringified = JSON.stringify({
                        browser: BrowserDetect.browser,
                        version: BrowserDetect.version,
                        OS: BrowserDetect.OS,
                        userAgent: navigator.userAgent,
                        appVersion: navigator.appVersion,
                        vendor: navigator.vendor,
                        platform: navigator.platform,
                        griddVersion: __APP_VERSION__,
                        pixelRatio: window.devicePixelRatio,
                      });
                      const blobbed = new Blob([stringified], { type: "text/plain" });
                      navigator.clipboard.write([new ClipboardItem({ "text/plain": blobbed })]);
                      notify("success", "success.copy.debug");
                    }}
                  >
                    {t("debug.info.copy")}
                  </TrackedMenuItem>
                </CommonTooltip>
                <Divider />
                {advancedMenu && (
                  <TrackedMenuItem
                    id="reset_welcome_button"
                    icon={<SwapIcon />}
                    onClick={() => {
                      batchGroupBy.start();
                      dispatch(
                        reset({
                          columns: welcomeTemplate.cols,
                          rows: welcomeTemplate.rows,
                          cells: welcomeTemplate.cells,
                          merged: welcomeTemplate.merged,
                        })
                      );
                      dispatch(init(welcomeTemplate.borders));
                      if (welcomeTemplate.cellStyles) {
                        dispatch(styleReset(welcomeTemplate.cellStyles));
                      }
                      batchGroupBy.end();
                      dispatch(storeSheets({ to: "localStorage" }));
                      dispatch(storeSheets({ to: "presentation" }));
                    }}
                  >
                    {t("menu.welcome")}
                  </TrackedMenuItem>
                )}
                <CommonTooltip content={t("tooltip.export")} positioning="after">
                  <TrackedMenuItem
                    id="export_button"
                    icon={<CopyIcon />}
                    onClick={async () => {
                      const state = store.getState();
                      const stringified = exportGriddDataToJSON(state);
                      const griddBlob = new Blob([stringified], { type: "text/plain" });
                      await navigator.clipboard.write([new ClipboardItem({ "text/plain": griddBlob })]);
                      notify("success", "success.copy.gridd");
                    }}
                  >
                    {t("menu.export")}
                  </TrackedMenuItem>
                </CommonTooltip>
                <CommonTooltip content={t("tooltip.import")} positioning="after">
                  <TrackedMenuItem
                    id="import_button"
                    icon={<ClipboardIcon />}
                    disabled={importing}
                    onClick={async () => {
                      setImporting(true);
                      let gridd;
                      try {
                        const content = await navigator.clipboard.read();
                        for (const item of content) {
                          const blob = await item.getType("text/plain");
                          if (blob) {
                            const text = await blob.text();
                            if (text) {
                              gridd = text;
                              break;
                            }
                          }
                        }
                        const griddObject = JSON.parse(gridd);
                        batchGroupBy.start();
                        dispatch(
                          reset({
                            columns: griddObject.cols,
                            rows: griddObject.rows,
                            cells: griddObject.cells,
                            merged: griddObject.merged,
                          })
                        );
                        dispatch(init(griddObject.borders));
                        if (griddObject.cellStyles) {
                          dispatch(styleReset(griddObject.cellStyles));
                        }
                        batchGroupBy.end();

                        dispatch(UndoActionCreators.clearHistory());
                        dispatch(selectionReset());
                        notify("success", "success.paste.gridd");
                      } catch (error) {
                        console.error("error importing table", { error, gridd });
                        notify("error", "error.paste.gridd");
                      } finally {
                        setImporting(false);
                      }
                    }}
                  >
                    {t("menu.import")}
                  </TrackedMenuItem>
                </CommonTooltip>
              </MenuList>
            </MenuPopover>
          </Menu>

          <Divider />

          <CommonTooltip content={t("tooltip.new.slide")} positioning="after">
            <TrackedMenuItem
              id="stack_menu_new_slide_button"
              icon={
                <PlusIcon
                  style={{
                    width: 24 * uiScale,
                    height: 24 * uiScale,
                  }}
                />
              }
              onClick={() => {
                insertSlideWithGridd();
              }}
              appearance="subtle"
              size="small"
            >
              {t("menu.slide")}
            </TrackedMenuItem>
          </CommonTooltip>

          <Menu>
            <MenuTrigger>
              <TrackedMenuItem id="delete_gridd_trigger" icon={<TrashIcon weight="light" />}>
                {t("menu.delete.trigger")}
              </TrackedMenuItem>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <CommonTooltip content={t("tooltip.templates")} positioning="after">
                  <TrackedMenuItem
                    id="stack_menu_templates_button"
                    icon={<GridNineIcon weight="light" />}
                    onClick={() => {
                      dispatch(toTemplates({ griddContentExist: true }));
                    }}
                    appearance="subtle"
                    size="small"
                  >
                    {t("menu.templates")}
                  </TrackedMenuItem>
                </CommonTooltip>
                <CommonTooltip content={t("tooltip.clear")} positioning="after">
                  <TrackedMenuItem
                    id="gridd_clear_text_button"
                    icon={<EraserIcon />}
                    onClick={() => {
                      dispatch(clear());
                    }}
                  >
                    {t("menu.clear")}
                  </TrackedMenuItem>
                </CommonTooltip>
              </MenuList>
            </MenuPopover>
          </Menu>

          <Divider />

          <CommonTooltip content={t("tooltip.styleSheet")} positioning="after">
            <TrackedMenuItem
              id="stack_menu_style_sheet_button"
              onClick={() => {
                dispatch(openStyleSheetDialog());
              }}
              icon={<PaintBrushBroadIcon weight="light" />}
              appearance="subtle"
              size="small"
            >
              {t("menu.stylesheet")}
            </TrackedMenuItem>
          </CommonTooltip>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default GriddMenu;
